import { useState } from 'react';
import { Stack, Group, Button, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import logger from '@ayocore/platform/common/helpers/logger';
import { usePhotoNameTemplateRepository } from '../../rpc';
import { makePhraseLabel } from './inspectionTemplates';

export interface PhotoNameTemplateFormModalProps {
  /** Вид работ, к которому добавляем шаблон. Опционально для useModal (weak-type). */
  workTypeId?: number;
  /** Вызывается после успешного создания (родитель закрывает модалку и обновляет список). */
  onSuccess?: () => void;
}

/**
 * Модалка добавления шаблона значения {object} имени фото к виду работ.
 *
 * Единая форма для двух точек вызова: кнопка «＋» в фото-табе инспекции и «Добавить»
 * в менеджере шаблонов. Подпись опциональна — если пусто, автоусечение из значения
 * (makePhraseLabel). is_system=false; автор — created_by_id (системный хук).
 *
 * Обёртки <Modal> нет — открывается через useModal; закрытие — по onSuccess у родителя.
 */
export function PhotoNameTemplateFormModal({
  workTypeId,
  onSuccess,
}: PhotoNameTemplateFormModalProps) {
  const repo = usePhotoNameTemplateRepository();
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const text = value.trim();
    if (!text || workTypeId == null) return;
    try {
      setBusy(true);
      await repo.create({
        work_type_id: workTypeId,
        value: text,
        label: label.trim() || makePhraseLabel(text),
        is_system: false,
      });
      onSuccess?.();
    } catch (err) {
      notifications.show({
        title: 'Ошибка',
        message: err instanceof Error ? err.message : 'Не удалось добавить шаблон',
        color: 'red',
      });
      logger.error(err, '[mws] PhotoNameTemplateFormModal.submit');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap='sm'>
      <TextInput
        size='xs'
        label='Название'
        placeholder='Короткий тайтл для кнопки (пусто → из текста)'
        value={label}
        onChange={e => setLabel(e.currentTarget.value)}
        disabled={busy}
      />
      <TextInput
        size='xs'
        label='Текст фразы'
        placeholder='Что подставляется в имя фото (напр. «Перо руля»)'
        value={value}
        onChange={e => setValue(e.currentTarget.value)}
        disabled={busy}
        data-autofocus
      />
      <Group justify='flex-end'>
        <Button
          size='xs'
          variant='light'
          disabled={busy || !value.trim()}
          loading={busy}
          onClick={() => void submit()}
        >
          Добавить
        </Button>
      </Group>
    </Stack>
  );
}
